import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SubHeader from '../../layout/components/SubHeader';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import { CLINIC_FEATURE_MODULES } from '../../../modules/clinicFeatures/clinicFeatureModules';
import { getEnabledClinicModules } from '../../../modules/clinicFeatures/clinicFeatureUtils';
import {
    FAQ_CATEGORY_GROUPS,
    PATIENT_FILES_GROUP_ID,
    buildFaqCategorySections,
    categoryMatchesFilter,
    getCategoryById,
    getChildCategories,
    getSupportOptions,
    getTopLevelCategories,
    getFaqItems,
} from './helpContent';
import './HelpSupport.css';

function HelpCategoryIcon({ icon, theme = 'primary', size = 'md' }) {
    return (
        <span
            className={`ecx-help-category-icon ecx-help-category-icon--${size} ecx-nav-icon ecx-nav-icon--${theme}`}
            aria-hidden="true"
        >
            <i className={`bi ${icon}`} />
        </span>
    );
}

function HelpEmptyState({ icon, title, description, theme = 'primary' }) {
    return (
        <div className="ecx-help-empty">
            <span className="ecx-help-empty__icon ecx-help-empty__icon--themed" aria-hidden="true">
                <HelpCategoryIcon icon={icon} theme={theme} size="lg" />
            </span>
            <h6 className="ecx-help-empty__title">{title}</h6>
            <p className="ecx-help-empty__text">{description}</p>
        </div>
    );
}

function FaqItem({ item, isOpen, onToggle }) {
    return (
        <div className="ecx-help-faq-item">
            <button
                type="button"
                className="ecx-help-faq-button"
                aria-expanded={isOpen}
                onClick={onToggle}
            >
                <i className="bi bi-question-circle text-primary flex-shrink-0 mt-1" />
                <span className="flex-grow-1">{item.question}</span>
                <i className="bi bi-chevron-down faq-chevron" />
            </button>
            {isOpen && (
                <div className="ecx-help-faq-body">{item.answer}</div>
            )}
        </div>
    );
}

function FaqCategoryBlock({ category, items, openFaqId, onToggleFaq, nested = false }) {
    return (
        <div className={`ecx-help-faq-section ${nested ? 'ecx-help-faq-section--nested' : ''}`}>
            <div className="d-flex align-items-center gap-2 ecx-help-faq-section__header">
                <HelpCategoryIcon icon={category.icon} theme={category.theme} size="sm" />
                <h6 className="mb-0 fw-bold">{category.title}</h6>
                <span className="badge bg-light text-muted ms-2 ecx-help-category-badge">
                    {items.length}
                </span>
            </div>
            {items.map((item) => (
                <FaqItem
                    key={item.id}
                    item={item}
                    isOpen={openFaqId === item.id}
                    onToggle={() => onToggleFaq(item.id)}
                />
            ))}
        </div>
    );
}

function SupportCard({ option }) {
    const action = option.external ? (
        <a
            href={option.href}
            className={`btn btn-sm btn-${option.color} text-white`}
            target="_blank"
            rel="noopener noreferrer"
        >
            {option.actionLabel}
            <i className="bi bi-box-arrow-up-right ms-1" />
        </a>
    ) : (
        <Link to={option.to} className={`btn btn-sm btn-${option.color} text-white`}>
            {option.actionLabel}
            <i className="bi bi-arrow-right ms-1" />
        </Link>
    );

    return (
        <div className="card border-0 shadow-sm ecx-help-support-card h-100">
            <div className="card-body d-flex flex-column">
                <div className="ecx-help-support-header d-flex align-items-center gap-3">
                    <HelpCategoryIcon icon={option.icon} theme={option.theme} size="lg" />
                    <div className="ecx-help-support-text min-w-0">
                        <h6 className="fw-semibold mb-0 ecx-help-support-title">{option.title}</h6>
                        <span className="text-muted small ecx-help-support-description">{option.description}</span>
                    </div>
                </div>
                <div className="ecx-help-support-action mt-auto pt-3">
                    {action}
                </div>
            </div>
        </div>
    );
}

function EnabledModulesPanel({ modules, isLoading }) {
    if (isLoading) {
        return (
            <div className="card shadow-sm border-0 mb-4 ecx-help-modules-card">
                <div className="card-body">
                    <div className="d-flex align-items-center gap-2 text-muted">
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Loading your clinic modules...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow-sm border-0 mb-4 ecx-help-modules-card">
            <div className="card-header bg-white py-3">
                <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                    <HelpCategoryIcon icon="bi-stars" theme="primary" size="sm" />
                    Your Enabled Modules
                </h6>
            </div>
            <div className="card-body">
                {modules.length === 0 ? (
                    <p className="text-muted mb-0 small">
                        No optional modules are currently enabled for your clinic. Contact EyeconX support
                        if you need access to features such as messaging, surveys, or online booking.
                    </p>
                ) : (
                    <div className="row g-3">
                        {modules.map((module) => (
                            <div key={module.id} className="col-12 col-md-6 col-xl-4">
                                <Link to={module.to} className="ecx-help-module-card text-decoration-none">
                                    <div className="d-flex align-items-start gap-3">
                                        <HelpCategoryIcon icon={module.icon} theme={module.theme} size="md" />
                                        <div className="min-w-0">
                                            <h6 className="fw-semibold mb-1 text-dark">{module.label}</h6>
                                            <p className="text-muted small mb-0">{module.description}</p>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function HelpSupport() {
    const clinicFeatures = useEyeconxStore((state) => state.clinicFeatures);
    const clinicFeaturesLoaded = useEyeconxStore((state) => state.clinicFeaturesLoaded);
    const [searchParams] = useSearchParams();
    const [activeSection, setActiveSection] = useState('faqs');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [expandedGroups, setExpandedGroups] = useState({ [PATIENT_FILES_GROUP_ID]: true });
    const [searchQuery, setSearchQuery] = useState('');
    const [openFaqId, setOpenFaqId] = useState(null);

    const faqItems = useMemo(() => getFaqItems(), []);
    const supportOptions = useMemo(
        () => getSupportOptions(clinicFeatures, clinicFeaturesLoaded),
        [clinicFeatures, clinicFeaturesLoaded]
    );

    const enabledModules = useMemo(
        () => getEnabledClinicModules(CLINIC_FEATURE_MODULES, clinicFeatures, clinicFeaturesLoaded),
        [clinicFeatures, clinicFeaturesLoaded]
    );

    const filteredFaqs = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        return faqItems.filter((item) => {
            if (!categoryMatchesFilter(item.categoryId, categoryFilter)) {
                return false;
            }

            if (!query) {
                return true;
            }

            return (
                item.question.toLowerCase().includes(query)
                || item.answer.toLowerCase().includes(query)
            );
        });
    }, [categoryFilter, searchQuery, faqItems]);

    const faqSections = useMemo(() => {
        if (searchQuery.trim()) {
            return null;
        }

        return buildFaqCategorySections(faqItems, categoryFilter);
    }, [categoryFilter, searchQuery, faqItems]);

    const toggleFaq = (id) => {
        setOpenFaqId((prev) => (prev === id ? null : id));
    };

    const toggleGroup = (groupId) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [groupId]: !prev[groupId],
        }));
    };

    const handleCategorySelect = (filterId) => {
        setCategoryFilter(filterId);

        const childCategory = getCategoryById(filterId);

        if (childCategory?.parentId) {
            setExpandedGroups((prev) => ({
                ...prev,
                [childCategory.parentId]: true,
            }));
        }
    };

    useEffect(() => {
        const categoryParam = searchParams.get('category');

        if (!categoryParam) {
            return;
        }

        const category = getCategoryById(categoryParam);

        if (!category) {
            return;
        }

        setActiveSection('faqs');
        setCategoryFilter(categoryParam);

        if (category.parentId) {
            setExpandedGroups((prev) => ({
                ...prev,
                [category.parentId]: true,
            }));
        }
    }, [searchParams]);

    const isGroupActive = (groupId) =>
        categoryFilter === groupId || getChildCategories(groupId).some((child) => child.id === categoryFilter);

    const hasFaqs = faqItems.length > 0;
    const hasSupportOptions = supportOptions.length > 0;

    return (
        <div className="ecx-page ecx-page--full-width help-support-page">
            <SubHeader
                title="Help & Support"
                description="FAQs, guides, and contact options for EyeconX"
                icon="bi-life-preserver"
                containerClass="w-100 px-4"
            />

            <div className="ecx-page-body ecx-help-page">
                <EnabledModulesPanel
                    modules={enabledModules}
                    isLoading={!clinicFeaturesLoaded}
                />

                <ul className="nav nav-pills mb-4 gap-2 ecx-help-nav">
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${activeSection === 'faqs' ? 'active' : ''}`}
                            onClick={() => setActiveSection('faqs')}
                        >
                            <i className="bi bi-journal-text me-2" />
                            FAQs
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${activeSection === 'support' ? 'active' : ''}`}
                            onClick={() => setActiveSection('support')}
                        >
                            <i className="bi bi-life-preserver me-2" />
                            Get Support
                        </button>
                    </li>
                </ul>

                {activeSection === 'faqs' && (
                    <div className="row g-3">
                        <div className="col-12 col-lg-3">
                            <div className="card shadow-sm border-0">
                                <div className="card-header bg-white py-3">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="bi bi-funnel me-2 text-primary" />
                                        Categories
                                    </h6>
                                </div>
                                <div className="card-body p-2">
                                    <div className="nav flex-column nav-pills ecx-help-nav ecx-help-category-nav">
                                        <button
                                            type="button"
                                            className={`nav-link text-start ${categoryFilter === 'all' ? 'active' : ''}`}
                                            onClick={() => handleCategorySelect('all')}
                                        >
                                            All topics
                                        </button>

                                        {FAQ_CATEGORY_GROUPS.map((group) => {
                                            const children = getChildCategories(group.id);
                                            const isExpanded = expandedGroups[group.id] ?? true;
                                            const groupActive = isGroupActive(group.id);

                                            return (
                                                <div key={group.id} className="ecx-help-category-group">
                                                    <div className="ecx-help-category-group__row">
                                                        <button
                                                            type="button"
                                                            className={`nav-link text-start ecx-help-category-group__toggle ${
                                                                groupActive ? 'is-active-group' : ''
                                                            }`}
                                                            onClick={() => toggleGroup(group.id)}
                                                            aria-expanded={isExpanded}
                                                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${group.title}`}
                                                        >
                                                            <i
                                                                className={`bi ${
                                                                    isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'
                                                                } ecx-help-category-group__chevron`}
                                                            />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`nav-link text-start flex-grow-1 ecx-help-category-group__label ${
                                                                categoryFilter === group.id ? 'active' : ''
                                                            } ${groupActive ? 'is-active-group' : ''}`}
                                                            onClick={() => handleCategorySelect(group.id)}
                                                        >
                                                            <HelpCategoryIcon
                                                                icon={group.icon}
                                                                theme={group.theme}
                                                                size="sm"
                                                            />
                                                            {group.title}
                                                        </button>
                                                    </div>

                                                    {isExpanded && (
                                                        <div className="ecx-help-category-children">
                                                            {children.map((child) => (
                                                                <button
                                                                    key={child.id}
                                                                    type="button"
                                                                    className={`nav-link text-start ecx-help-category-child ${
                                                                        categoryFilter === child.id ? 'active' : ''
                                                                    }`}
                                                                    onClick={() => handleCategorySelect(child.id)}
                                                                >
                                                                    <HelpCategoryIcon
                                                                        icon={child.icon}
                                                                        theme={child.theme}
                                                                        size="sm"
                                                                    />
                                                                    {child.title}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {getTopLevelCategories().map((category) => (
                                            <button
                                                key={category.id}
                                                type="button"
                                                className={`nav-link text-start ${categoryFilter === category.id ? 'active' : ''}`}
                                                onClick={() => handleCategorySelect(category.id)}
                                            >
                                                <HelpCategoryIcon
                                                    icon={category.icon}
                                                    theme={category.theme}
                                                    size="sm"
                                                />
                                                {category.title}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-9">
                            <div className="card shadow-sm border-0 ecx-help-faq-card">
                                <div className="card-header bg-white py-3">
                                    <div className="row g-2 align-items-center">
                                        <div className="col">
                                            <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                                                <HelpCategoryIcon
                                                    icon="bi-life-preserver"
                                                    theme="primary"
                                                    size="sm"
                                                />
                                                Frequently Asked Questions
                                            </h6>
                                        </div>
                                        <div className="col-12 col-md-auto">
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text bg-white border-end-0">
                                                    <i className="bi bi-search text-muted" />
                                                </span>
                                                <input
                                                    type="search"
                                                    className="form-control border-start-0"
                                                    placeholder="Search FAQs..."
                                                    value={searchQuery}
                                                    onChange={(event) => setSearchQuery(event.target.value)}
                                                    disabled={!hasFaqs}
                                                />
                                                {searchQuery && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => setSearchQuery('')}
                                                        aria-label="Clear search"
                                                    >
                                                        <i className="bi bi-x-lg" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    {!hasFaqs ? (
                                        <HelpEmptyState
                                            icon="bi-journal-text"
                                            theme="primary"
                                            title="FAQs coming soon"
                                            description="Help articles and frequently asked questions will be added here."
                                        />
                                    ) : filteredFaqs.length === 0 ? (
                                        <HelpEmptyState
                                            icon="bi-search"
                                            title="No matching FAQs"
                                            description="No FAQs match your search. Try another keyword or category."
                                        />
                                    ) : faqSections ? (
                                        faqSections.map((section) =>
                                            section.type === 'group' ? (
                                                <div key={section.id} className="ecx-help-faq-group">
                                                    <div className="d-flex align-items-center ecx-help-faq-group__header">
                                                        <HelpCategoryIcon
                                                            icon={section.icon}
                                                            theme={section.theme}
                                                            size="md"
                                                        />
                                                        <h5 className="mb-0 fw-bold">{section.title}</h5>
                                                    </div>
                                                    {section.children.map((child) => (
                                                        <FaqCategoryBlock
                                                            key={child.id}
                                                            category={child}
                                                            items={child.items}
                                                            openFaqId={openFaqId}
                                                            onToggleFaq={toggleFaq}
                                                            nested
                                                        />
                                                    ))}
                                                </div>
                                            ) : (
                                                <FaqCategoryBlock
                                                    key={section.id}
                                                    category={section}
                                                    items={section.items}
                                                    openFaqId={openFaqId}
                                                    onToggleFaq={toggleFaq}
                                                />
                                            )
                                        )
                                    ) : (
                                        filteredFaqs.map((item) => {
                                            const category = getCategoryById(item.categoryId);

                                            return (
                                                <div key={item.id} className="mb-3">
                                                    {category && (
                                                        <span className="ecx-help-search-category">
                                                            <HelpCategoryIcon
                                                                icon={category.icon}
                                                                theme={category.theme}
                                                                size="sm"
                                                            />
                                                            <span className="badge bg-light text-muted ecx-help-category-badge">
                                                                {category.title}
                                                            </span>
                                                        </span>
                                                    )}
                                                    <FaqItem
                                                        item={item}
                                                        isOpen={openFaqId === item.id}
                                                        onToggle={() => toggleFaq(item.id)}
                                                    />
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeSection === 'support' && (
                    <>
                        <div className="card shadow-sm border-0 mb-4 ecx-help-intro-card">
                            <div className="card-body">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="ecx-help-support-icon flex-shrink-0">
                                        <HelpCategoryIcon icon="bi-life-preserver" theme="primary" size="lg" />
                                    </div>
                                    <div>
                                        <h5 className="fw-semibold mb-1">Need more help?</h5>
                                        <p className="text-muted mb-0 small">
                                            Support resources and quick links will appear here soon.
                                            For urgent issues, contact EyeconX support with your clinic name.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {hasSupportOptions ? (
                            <div className="row g-3 ecx-help-support-grid">
                                {supportOptions.map((option) => (
                                    <div key={option.id} className="col-12 col-md-6 col-xl-4">
                                        <SupportCard option={option} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="card shadow-sm border-0">
                                <div className="card-body">
                                    <HelpEmptyState
                                        icon="bi-life-preserver"
                                        title="Support resources coming soon"
                                        description="Contact options and quick links to common tasks will be added here."
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default HelpSupport;
